import resolveComposerScopeKey from './lib/resolveComposerScopeKey.js';
/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import ATTACHMENT_OVERFLOW_STRATEGY from '@/lib/composer/ATTACHMENT_OVERFLOW_STRATEGY.js';
import ORG_SETTINGS_MODAL_KEY from '@/lib/settings/ORG_SETTINGS_MODAL_KEY.js';
import SETTINGS_MODAL_KEY from '@/lib/settings/SETTINGS_MODAL_KEY.js';
import getComposerFocusSurface from '@/lib/getComposerFocusSurface.js';
import getPieveRouteMatch from '@/lib/routes/getPieveRouteMatch.js';
import { threadContentMap } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import { THREAD_SURFACE_MODE_CREATING_THREAD, THREAD_SURFACE_MODE_OPENING_THREAD, THREAD_SURFACE_STATE_OPENING_THREAD__HYDRATING_CONTENT, THREAD_SURFACE_STATE_OPENING_THREAD__MASKING_CONTENT, THREAD_SURFACE_STATE_OPENING_THREAD__MEASURING_LAYOUT, THREAD_SURFACE_STATE_OPENING_THREAD__RESTORING_SCROLL_POSITION, THREAD_SURFACE_STATE_OPENING_THREAD__REVEALING_CONTENT, THREAD_SURFACE_STATE_THREAD__SUBMITTED_COMPLETE, THREAD_SURFACE_STATE_THREAD__SUBMITTED_PREPOSITIONING_VIEWPORT, THREAD_SURFACE_STATE_THREAD__SUBMITTED_REVEALING_USER_BUBBLE, THREAD_SURFACE_STATE_THREAD__SUBMITTED_SETTLING_ASSISTANT, THREAD_SURFACE_STATE_THREAD__SUBMITTED_SHOWING_WORKING_INDICATOR, THREAD_SURFACE_STATE_THREAD__SUBMITTED_STREAMING_ASSISTANT } from '@/lib/threadSurface/threadSurfacePresentationConstants.js';
import { getDeep } from '@shared/utils';
import parseLocationSearch from '@/redux/selectors/lib/parseLocationSearch.js';

const EMPTY_OBJECT = Object.freeze({});
const EMPTY_ARRAY = Object.freeze([]);

export const pathname = (state) => state?.router?.location?.pathname || '';
export const locationKey = (state) => state?.router?.location?.key;
export const locationHash = (state) => state?.router?.location?.hash || '';
export const fragmentParam = createSelector([locationHash, (_, key) => key], (hash, key) => new URLSearchParams(hash.slice(1)).get(key));
export const platformLinkProof = (state, verificationState) => (verificationState && state?.ui?.platformIdentityVerification?.meta?.verificationState === verificationState ? state.ui.platformIdentityVerification.data : null);
export const locationSearch = (state) => state?.router?.location?.search || '';
export const activeRouteId = createSelector([pathname], (currentPathname) => {
  return getPieveRouteMatch(currentPathname)?.route?.routeId;
});
export const fixedWidth = (state) => state?.ui?.fixedWidth;
export const params = createSelector([pathname], (currentPathname) => {
  return getPieveRouteMatch(currentPathname)?.params || EMPTY_OBJECT;
});
export const param = createSelector([params, (_, key) => key], (routeParams, key) => {
  if (key === undefined) {
    return routeParams;
  }

  return routeParams?.[key];
});
export const query = createSelector([locationSearch, (_, key) => key], (search, key) => {
  const parsedQuery = parseLocationSearch(search);

  if (key === undefined) {
    return parsedQuery;
  }

  return parsedQuery?.[key];
});
export const isWorkspaceCompanyListLoading = (state) => state?.ui?.isWorkspaceCompanyListLoading === true;
export const workspaceCompanyListError = (state) => state?.ui?.workspaceCompanyListError || '';
export const workspaceSwitchingCompanyId = (state) => state?.ui?.workspaceSwitchingCompanyId || null;
export const workspaceSidebarCollapsed = (state) => state?.ui?.workspaceSidebarCollapsed === true;
export const workflowSurfaceSingleColumnView = (state) => (state?.ui?.workflowSurfaceSingleColumnView === 'workflow' ? 'workflow' : 'chat');
export const desktopWorkspaceSidebarCollapsed = (state) => state?.ui?.desktopWorkspaceSidebarCollapsed === true;
export const desktopSidebarPeek = (state) => state?.ui?.desktopSidebarPeek ?? 'closed';
export const activeChecklistByThreadId = (state) => state?.ui?.activeChecklistByThreadId || EMPTY_OBJECT;
export const olderThreadEventsByThreadId = (state) => state?.ui?.olderThreadEventsByThreadId || EMPTY_OBJECT;
export const adminThreadViewId = (state) => state?.ui?.adminThreadViewId || null;
export const planRunReadiness = (state) => state?.ui?.planRunReadiness || null;
export const isPlanRunReadinessRefreshing = (state) => (Number(state?.ui?.planRunReadinessRefreshCount) || 0) > 0;
// Whether this device can send to a plan thread's Mac (passthrough L44): the server's answer, or null before it.
export const planThreadRemote = (state, threadId) => state?.ui?.planThreadRemoteByThreadId?.[String(threadId || '').trim()] || null;
export const planRunSelectionByScopeKey = (state) => state?.ui?.planRunSelectionByScopeKey || EMPTY_OBJECT;
export const mainThreadTerminalOpenByThreadId = (state) => state?.ui?.mainThreadTerminalOpenByThreadId || EMPTY_OBJECT;
export const mainThreadSubagentPaneHiddenByScope = (state) => state?.ui?.mainThreadSubagentPaneHiddenByScope || EMPTY_OBJECT;
export const composerAttachmentsByScope = (state) => state?.ui?.composerAttachmentsByScope || EMPTY_OBJECT;
export const composerAttachmentContextBudgetByScope = (state) => state?.ui?.composerAttachmentContextBudgetByScope || EMPTY_OBJECT;
export const composerAttachmentOverflowStrategyByScope = (state) => state?.ui?.composerAttachmentOverflowStrategyByScope || EMPTY_OBJECT;
export const composerAttachmentSkillSuggestionRequestByScope = (state) => state?.ui?.composerAttachmentSkillSuggestionRequestByScope || EMPTY_OBJECT;
export const composerPendingInteractionIndexByScope = (state) => state?.ui?.composerPendingInteractionIndexByScope || EMPTY_OBJECT;
export const requestLoadersByKey = (state) => state?.ui?.requestLoadersByKey || EMPTY_OBJECT;
export const websiteSignupSubtitles = (state) => state?.ui?.websiteSignupSubtitles || EMPTY_ARRAY;
export const websiteSignupSubtitle = (state, handoffId) => {
  const normalizedHandoffId = String(handoffId || '').trim();

  if (!normalizedHandoffId) {
    return '';
  }

  const row = websiteSignupSubtitles(state).find((item) => item?.handoffId === normalizedHandoffId);
  return String(row?.subtitle || '').trim();
};
export const crossThreadEmptyStateTransitionState = (state) => state?.ui?.crossThreadEmptyStateTransition || null;
export const emptyStateSubmissionState = (state) => state?.ui?.emptyStateSubmission || null;
export const mainThreadTerminalOpenState = (state, threadId) => {
  const normalizedThreadId = String(threadId || '').trim();

  if (!normalizedThreadId) {
    return null;
  }

  const open = mainThreadTerminalOpenByThreadId(state)?.[normalizedThreadId];

  if (open === true) {
    return true;
  }

  if (open === false) {
    return false;
  }

  return null;
};
export const isMainThreadTerminalOpen = (state, threadId) => {
  return mainThreadTerminalOpenState(state, threadId) === true;
};
export const mainThreadSubagentPaneHiddenContextIdsForScope = (state, scopeKey) => {
  const normalizedScopeKey = String(scopeKey || '').trim();

  if (!normalizedScopeKey) {
    return EMPTY_OBJECT;
  }

  return mainThreadSubagentPaneHiddenByScope(state)?.[normalizedScopeKey] || EMPTY_OBJECT;
};
export const composerPendingInteractionIndex = (state, threadId, scopeKey = null) => {
  const normalizedScopeKey = resolveComposerScopeKey({
    scopeKey,
    threadId
  });

  return Number(composerPendingInteractionIndexByScope(state)?.[normalizedScopeKey] || 0);
};
export const composerInputPlaceholder = (state, scopeKey = null, threadId = null) => {
  const normalizedThreadId = String(threadId || '').trim();
  const threadContent = normalizedThreadId ? threadContentMap(state)?.[normalizedThreadId] : null;

  /* "Reply..." only once the thread actually has activity — an empty thread keeps the
     surface-mode prompt (e.g. analytics tasks suggest analytics things). */
  if (normalizedThreadId && Array.isArray(threadContent?.activity) && threadContent.activity.length > 0) {
    return 'Reply...';
  }

  const threadPlaceholder = String(threadContent?.composer?.placeholder || '').trim();

  if (threadPlaceholder) {
    return threadPlaceholder;
  }

  /* Tri-state: until the thread's events have hydrated we cannot tell "new thread" from
     "existing thread still loading" — return null so the composer suppresses its new-thread
     prompt instead of flashing it before 'Reply...' appears. '' means known-empty. */
  if (normalizedThreadId && threadContent?.eventsHydrated !== true) {
    return null;
  }

  return '';
};
export const composerAttachmentOverflowStrategy = (state, scopeKey = null, threadId = null) => {
  const normalizedScopeKey = resolveComposerScopeKey({
    scopeKey,
    threadId
  });

  return composerAttachmentOverflowStrategyByScope(state)?.[normalizedScopeKey] || ATTACHMENT_OVERFLOW_STRATEGY.truncate;
};
export const composerAttachmentEntries = (state, scopeKey = null, threadId = null) => {
  const normalizedScopeKey = resolveComposerScopeKey({
    scopeKey,
    threadId
  });

  const items = composerAttachmentsByScope(state)?.[normalizedScopeKey];

  return Array.isArray(items) ? items : EMPTY_ARRAY;
};
export const composerAttachmentContextBudget = (state, scopeKey = null, threadId = null) => {
  const normalizedScopeKey = resolveComposerScopeKey({
    scopeKey,
    threadId
  });

  return composerAttachmentContextBudgetByScope(state)?.[normalizedScopeKey] || null;
};
export const composerAttachmentSkillSuggestionRequest = (state, scopeKey = null, threadId = null) => {
  const normalizedScopeKey = resolveComposerScopeKey({
    scopeKey,
    threadId
  });

  return composerAttachmentSkillSuggestionRequestByScope(state)?.[normalizedScopeKey] || null;
};
export const isMainThreadSubagentPaneHidden = (state, scopeKey, contextId) => {
  const normalizedContextId = String(contextId || '').trim();

  if (!normalizedContextId) {
    return false;
  }

  return mainThreadSubagentPaneHiddenContextIdsForScope(state, scopeKey)?.[normalizedContextId] === true;
};
export const prefs = (state) => state?.ui?.prefs;
export const prefsRequestStatus = (state) => state?.ui?.prefsRequestStatus || 'idle';
export const settingsUiState = (state) => state?.ui?.settings || null;
export const isSettingsLoading = (state) => settingsUiState(state)?.isLoading === true;
export const isSettingsSaving = (state) => settingsUiState(state)?.isSaving === true;
export const settingsError = (state) => settingsUiState(state)?.error || '';
export const hasResolvedInitialSettings = (state) => settingsUiState(state)?.hasResolvedInitialSettings === true;
export const settingsArchivedThreadsRefreshKey = (state) => Number(settingsUiState(state)?.archivedThreadsRefreshKey || 0);
export const threadContextPreviewResponse = (state) => state?.ui?.threadContextPreview || null;
export const threadContextPreviewPayload = (state) => (threadContextPreviewResponse(state)?.success === true ? threadContextPreviewResponse(state)?.data || null : null);
export const threadContextPreviewError = (state) => {
  const response = threadContextPreviewResponse(state);

  if (response?.success !== false) {
    return '';
  }

  return String(response?.message || 'Failed to load context preview');
};
export const threadRunInteractionResponse = (state) => state?.ui?.threadRunInteractionResponse || null;
export const pendingSkillDraftItem = (state) => state?.ui?.pendingSkillDraftItem || null;
export const mainThreadHydrationSettled = (state) => state?.ui?.mainThreadHydrationSettled === true;
export const selectedThreadId = (state) => {
  const normalizedThreadId = String(state?.ui?.selectedThreadId || '').trim();
  return normalizedThreadId || null;
};
export const activeProjectId = (state) => {
  const normalizedProjectId = String(state?.ui?.activeProjectId || '').trim();
  return normalizedProjectId || null;
};
export const threadSurfacePresentationState = (state) => {
  const value = state?.ui?.threadSurfacePresentation;

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value;
};
export const threadSurfaceSignalQueueState = (state) => {
  return Array.isArray(state?.ui?.threadSurfaceSignalQueue) ? state.ui.threadSurfaceSignalQueue : EMPTY_ARRAY;
};
export const nextThreadSurfaceSignal = (state) => {
  const queue = Array.isArray(state?.ui?.threadSurfaceSignalQueue) ? state.ui.threadSurfaceSignalQueue : [];

  return queue[0] || null;
};
export const threadSurfaceSubmitRequestState = (state) => {
  const value = state?.ui?.threadSurfaceSubmitRequest;

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value;
};
export const threadSurfaceSubmitRequestForThread = (state, threadId) => {
  const normalizedThreadId = String(threadId || '').trim();
  const request = threadSurfaceSubmitRequestState(state);

  if (!normalizedThreadId || String(request?.threadId || '').trim() !== normalizedThreadId) {
    return null;
  }

  return request;
};
export const threadSurfaceSubmissionTokenForThread = (state, threadId) => {
  const submitRequest = threadSurfaceSubmitRequestForThread(state, threadId);

  if (submitRequest?.submissionToken) {
    return submitRequest.submissionToken;
  }

  return emptyStateSubmissionForThread(state, threadId)?.token || null;
};
export const threadSurfaceMode = (state) => threadSurfacePresentationState(state)?.mode || null;
export const threadSurfaceModeState = (state) => threadSurfacePresentationState(state)?.modeState || null;
export const threadSurfaceScrollOwner = (state) => threadSurfacePresentationState(state)?.scrollOwner || null;
export const threadSurfaceAnchorPresentationKey = (state) => threadSurfacePresentationState(state)?.anchorPresentationKey || '';
export const threadSurfaceSpacerBudget = (state) => threadSurfacePresentationState(state)?.spacerBudget || 0;
export const threadSurfaceHydrationRestTarget = (state) => threadSurfacePresentationState(state)?.hydrationRestTarget || null;
export const threadSurfaceQueueVisibleCount = (state) => threadSurfacePresentationState(state)?.queueVisibleCount || 0;
export const threadSurfaceQueueEnteringKey = (state) => threadSurfacePresentationState(state)?.queueEnteringKey || '';
export const threadSurfaceQueueHiddenAnchorKey = (state) => threadSurfacePresentationState(state)?.queueHiddenAnchorKey || '';
export const threadSurfaceIsHydrationVisible = (state) => threadSurfacePresentationState(state)?.isHydrationVisible === true;
export const threadSurfaceViewportState = (state) => {
  const value = state?.ui?.threadSurfaceViewport;

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return EMPTY_OBJECT;
  }

  return value;
};
export const threadSurfaceViewportScrollTop = (state) => Number(threadSurfaceViewportState(state)?.scrollTop) || 0;
export const threadSurfaceViewportDistanceToBottom = (state) => Number(threadSurfaceViewportState(state)?.distanceToBottom) || 0;
export const threadSurfaceViewportClientHeight = (state) => Number(threadSurfaceViewportState(state)?.clientHeight) || 0;
export const isThreadSurfaceViewportNearBottom = (state) => threadSurfaceViewportDistanceToBottom(state) <= 32;
export const isThreadSurfaceViewportBeyondJumpThreshold = (state) => threadSurfaceViewportDistanceToBottom(state) > 240;
export const shouldMaskThreadSurfaceContent = (state) => {
  const modeState = threadSurfaceModeState(state);

  return modeState === THREAD_SURFACE_STATE_OPENING_THREAD__MASKING_CONTENT || modeState === THREAD_SURFACE_STATE_OPENING_THREAD__HYDRATING_CONTENT || modeState === THREAD_SURFACE_STATE_OPENING_THREAD__MEASURING_LAYOUT || modeState === THREAD_SURFACE_STATE_OPENING_THREAD__RESTORING_SCROLL_POSITION || modeState === THREAD_SURFACE_STATE_OPENING_THREAD__REVEALING_CONTENT;
};
export const isThreadSurfaceOpening = (state) => threadSurfaceMode(state) === THREAD_SURFACE_MODE_OPENING_THREAD;
export const isThreadSurfaceInSubmittedTurn = (state) => {
  const mode = threadSurfaceMode(state);
  const modeState = threadSurfaceModeState(state);

  if (mode === THREAD_SURFACE_MODE_CREATING_THREAD) {
    return true;
  }

  return modeState === THREAD_SURFACE_STATE_THREAD__SUBMITTED_PREPOSITIONING_VIEWPORT || modeState === THREAD_SURFACE_STATE_THREAD__SUBMITTED_REVEALING_USER_BUBBLE || modeState === THREAD_SURFACE_STATE_THREAD__SUBMITTED_SHOWING_WORKING_INDICATOR || modeState === THREAD_SURFACE_STATE_THREAD__SUBMITTED_STREAMING_ASSISTANT || modeState === THREAD_SURFACE_STATE_THREAD__SUBMITTED_SETTLING_ASSISTANT || modeState === THREAD_SURFACE_STATE_THREAD__SUBMITTED_COMPLETE;
};
export const threadSurfaceRouteHydrationState = (state) => {
  const value = state?.ui?.threadSurfaceRouteHydration;

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value;
};
export const isAutoPendingNewThread = (state) => state?.ui?.isAutoPendingNewThread === true;
export const eventKey = (state, key) => state?.ui?.eventKeys?.[key] || 0;
export const activeAutomationsPane = (state) => {
  const normalizedValue = String(state?.ui?.automations?.activePane || '')
    .trim()
    .toLowerCase();

  if (normalizedValue === 'triggers') {
    return 'triggers';
  }

  if (normalizedValue === 'workflows') {
    return 'workflows';
  }

  return 'schedules';
};
export const threadChecklistOverride = (state, threadId) => {
  const normalizedThreadId = String(threadId || '').trim();

  if (!normalizedThreadId) {
    return undefined;
  }

  return activeChecklistByThreadId(state)?.[normalizedThreadId];
};
export const threadOlderEventsState = (state, threadId) => {
  const normalizedThreadId = String(threadId || '').trim();

  if (!normalizedThreadId) {
    return null;
  }

  return olderThreadEventsByThreadId(state)?.[normalizedThreadId] || null;
};
export const crossThreadEmptyStateTransition = (state, threadId) => {
  const normalizedThreadId = String(threadId || '').trim();
  const transition = crossThreadEmptyStateTransitionState(state);

  if (!normalizedThreadId) {
    return null;
  }

  if (String(transition?.targetThreadId || '').trim() !== normalizedThreadId) {
    return null;
  }

  return transition;
};
export const emptyStateSubmissionForSurface = (state, surface) => {
  const normalizedSurface = getComposerFocusSurface(surface);
  const submission = emptyStateSubmissionState(state);

  if (String(submission?.sourceSurface || '').trim() !== normalizedSurface) {
    return null;
  }

  return submission;
};
export const isEmptyStateSubmissionActiveForSurface = (state, surface) => {
  return emptyStateSubmissionForSurface(state, surface) !== null;
};
export const emptyStateSubmissionForThread = (state, threadId) => {
  const normalizedThreadId = String(threadId || '').trim();
  const submission = emptyStateSubmissionState(state);

  if (!normalizedThreadId) {
    return null;
  }

  if (String(submission?.targetThreadId || '').trim() !== normalizedThreadId) {
    return null;
  }

  return submission;
};
export const isEmptyStateSubmissionActiveForThread = (state, threadId) => {
  return emptyStateSubmissionForThread(state, threadId) !== null;
};
export const threadHasOlderEvents = (state, threadId) => threadOlderEventsState(state, threadId)?.hasOlder === true;
export const threadLoadingOlderEvents = (state, threadId) => threadOlderEventsState(state, threadId)?.loadingOlder === true;
export const composerFocusRequestId = (state, surface) => {
  const normalizedSurface = getComposerFocusSurface(surface);
  return Number(state?.ui?.composerFocusRequestBySurface?.[normalizedSurface] || 0);
};
export const primaryActionRequest = (state) => state?.ui?.primaryActionRequest || null;
export const skillDraftThreadStartRequest = (state) => state?.ui?.skillDraftThreadStartRequest || null;
export const modals = (state) => state?.ui?.modals || EMPTY_OBJECT;
export const automationsUiState = (state) => state?.ui?.automations || EMPTY_OBJECT;
export const automationTriggerSelectionId = (state) => automationsUiState(state)?.triggerSelectionId || null;
export const automationScheduleSelectionId = (state) => automationsUiState(state)?.scheduleSelectionId || null;
export const automationScheduleRunSelectionContext = (state) => automationsUiState(state)?.scheduleRunSelectionContext || null;
export const automationTriggerRunSelectionContext = (state) => automationsUiState(state)?.triggerRunSelectionContext || null;
export const modal = (state, key) => {
  const modalKey = String(key || '').trim();

  if (!modalKey) {
    return null;
  }

  return modals(state)?.[modalKey] || null;
};
export const isModalOpen = (state, key) => modal(state, key)?.open === true;
export const settingsModal = (state) => modal(state, SETTINGS_MODAL_KEY);
export const orgSettingsModal = (state) => modal(state, ORG_SETTINGS_MODAL_KEY);
export const activeSettingsLikeModalKey = (state) => {
  if (settingsModal(state)?.open === true) {
    return SETTINGS_MODAL_KEY;
  }

  if (orgSettingsModal(state)?.open === true) {
    return ORG_SETTINGS_MODAL_KEY;
  }

  return null;
};
/* Mirror of `query(state, key)` for surfaces shared by the two settings-like
   modals: reads one filter value from whichever settings-like modal is open. */
export const settingsLikeModalValue = (state, key) => {
  const modalKey = activeSettingsLikeModalKey(state);

  if (!modalKey) {
    return null;
  }

  return modal(state, modalKey)?.[String(key || '').trim()] ?? null;
};
export const automationWorkflowRunLogsModal = (state) => modals(state)?.automationWorkflowRunLogs || null;
// A test's results opened from the Test window: a second modal over it, so closing it goes back to the test.
export const workflowTestResultsModal = (state) => modals(state)?.workflowTestResults || null;
// The workflow's saved test ({ steps, updatedOn? }) as this window holds it; undefined when it hasn't heard yet.
export const workflowTestSavedById = (state) => state?.ui?.workflowTestSavedByWorkflowId;
export const workflowRunEnvironmentIdById = (state) => state?.ui?.workflowRunEnvironmentIdByWorkflowId;
export const workflowDraftReadFailedThreadId = (state) => state?.ui?.workflowDraftReadFailedThreadId || null;
export const workflowTestSetup = (state, workflowId) => state?.ui?.workflowTestSetupByWorkflowId?.[String(workflowId || '').trim()] || null;
export const activeContextMenu = (state) => state?.ui?.activeContextMenu || null;
export const requestLoader = (state, key) => {
  const normalizedKey = String(key || '').trim();

  if (!normalizedKey) {
    return null;
  }

  return requestLoadersByKey(state)?.[normalizedKey] || null;
};
export const isRequestLoading = (state, key) => requestLoader(state, key)?.isLoading === true;
export const toolTraceInspector = (state) => state?.ui?.toolTraceInspector || EMPTY_OBJECT;
export const toolTraceInspectorExpandedRunId = (state) => toolTraceInspector(state)?.expandedRunId || null;
export const toolTraceInspectorPopoverLayout = (state) => toolTraceInspector(state)?.popoverLayout || null;
export const isModelPickerHowItWorksOpen = (state) => state?.ui?.modelPickerHowItWorksOpen === true;
export const analyticsTemplatePublish = (state) => state?.ui?.analyticsTemplatePublish || EMPTY_OBJECT;
export const analyticsTemplateSharingError = (state, scopeKey) => (state?.ui?.analyticsTemplateSharingScopeKey === scopeKey ? state.ui.analyticsTemplateSharingError : null);
export const artifactSlider = (state) => state?.ui?.artifactSlider || EMPTY_OBJECT;
export const isArtifactSliderOpen = (state) => artifactSlider(state)?.open === true;
export const artifactSliderSelectedKey = (state) => artifactSlider(state)?.selectedKey || null;
export const pref = (state, key, callback) => {
  const items = prefs(state);
  const value = key ? getDeep(key, items) : items;
  return callback ? callback(value) : value;
};

// --- Voice recording singleton selectors ---
export const voiceRecording = (state) => state?.ui?.voiceRecording || EMPTY_OBJECT;
export const isVoiceRecordingActive = (state) => voiceRecording(state)?.isRecording === true || voiceRecording(state)?.isRecordingStarting === true;
export const isVoiceRecording = (state) => voiceRecording(state)?.isRecording === true;
export const isVoiceRecordingStarting = (state) => voiceRecording(state)?.isRecordingStarting === true;
export const isVoiceTranscribing = (state) => voiceRecording(state)?.isTranscribing === true;
export const voiceRecordingSignalLevel = (state) => Math.min(1, Math.max(0, Number(voiceRecording(state)?.signalLevel || 0)));
export const voiceRecordingDuration = (state) => Math.max(0, Number(voiceRecording(state)?.duration || 0));
export const voiceRecordingStartRequestId = (state) => Math.max(0, Number(voiceRecording(state)?.startRequestId || 0));
export const voiceRecordingStopRequestId = (state) => Math.max(0, Number(voiceRecording(state)?.stopRequestId || 0));
export const voiceRecordingStopSubmitAfter = (state) => voiceRecording(state)?.stopSubmitAfter === true;
export const voiceRecordingChunks = (state) => (Array.isArray(voiceRecording(state)?.chunks) ? voiceRecording(state).chunks : []);
export const voiceRecordingRequestScopeKey = (state) => String(voiceRecording(state)?.requestScopeKey || '').trim();
export const voiceRecordingScopeKey = (state) => String(voiceRecording(state)?.scopeKey || '').trim();
export const isVoiceRecordingCaptureForScope = (state, scopeKey) => {
  return (isVoiceRecordingActive(state) || isVoiceTranscribing(state)) && voiceRecordingScopeKey(state) === String(scopeKey || '').trim();
};

// --- Composer send request singleton ---
export const composerSendRequestId = (state) => Math.max(0, Number(state?.ui?.composerSendRequestId || 0));
export const composerSendRequestScopeKey = (state) => String(state?.ui?.composerSendRequestScopeKey || '').trim();
export const composerSubmittedId = (state) => Math.max(0, Number(state?.ui?.composerSubmittedId || 0));
export const composerSubmittedScopeKey = (state) => String(state?.ui?.composerSubmittedScopeKey || '').trim();

// --- Derived composer selectors (no setting, just reading) ---
// Typing and sending always work; the composer is disabled only while an empty-state submission is in flight for that surface.
export const composerIsDisabled = (state, { surfaceMode = '' } = {}) => isEmptyStateSubmissionActiveForSurface(state, surfaceMode);

export const devOverrideGroups = (state) => state?.ui?.devOverrides?.groups || EMPTY_ARRAY;
export const devOverrideValues = (state) => state?.ui?.devOverrides?.overrides || EMPTY_OBJECT;

/* Request identity is UI state; a late failure cannot replace a new editor or library query. */
export const analyticsTemplateMutationError = (state, templateId) => (state?.ui?.analyticsTemplateMutation?.meta?.templateId === templateId ? state.ui.analyticsTemplateMutation.error : null);
export const analyticsTemplatesListError = (state, listKey) => {
  const error = state?.ui?.analyticsTemplatesListError;
  return error && error.meta?.listKey === listKey ? error.message || 'Could not load templates. Try again.' : null;
};
